import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React, { type ReactElement } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { currencyOptions } from '~/domains/taxonomies/types'
import { Dropdown } from './Dropdown'
import { PasswordHintInput } from './Elements/PasswordHint'
import {
  errorMessage,
  input,
  inputDate,
  inputDisabled,
  inputError,
  inputExtraSmall,
  inputField,
  inputHasCurrencyDropdown,
  inputHasLeadingIcon,
  inputHasLeadingText,
  inputHasTrailingIcon,
  inputHasTrailingText,
  inputLabel,
  inputLeadingIcon,
  inputLeadingText,
  inputPassword,
  inputSmall,
  inputTrailingIcon,
  inputTrailingText,
  inputWrapper,
  passwordVisibilityTrigger,
  passwordVisibilityTriggerOnError
} from './TextInput.css'

export interface TextInputProps
  extends React.HTMLProps<HTMLInputElement>,
    InputInFormProps {
  label?: string
  name?: string
  hasPasswordHint?: boolean
  standalone?: boolean
  wrapper?: boolean
  small?: boolean // Deprecated, use inputSize instead
  inputSize?: 'small' | 'extraSmall'
  noWrapperClassName?: string
  leadingIcon?: IconDefinition
  leadingText?: string
  trailingIcon?: IconDefinition
  trailingText?: string
  currencyInputName?: string | null
}

export const TextInput = ({
  control,
  register,
  label,
  name,
  id,
  hasPasswordHint,
  type,
  standalone = false,
  wrapper = true,
  small = false,
  inputSize,
  error,
  noWrapperClassName,
  leadingIcon,
  leadingText,
  trailingIcon,
  trailingText,
  currencyInputName,
  disabled,
  watch,
  setValue,
  ...rest
}: TextInputProps): ReactElement => {
  const [passwordVisibility, setPasswordVisibility] = React.useState(false)
  let attributes = { ...rest }
  if (
    !standalone &&
    register !== undefined &&
    control !== undefined &&
    name !== undefined
  ) {
    attributes = { ...rest, ...register(name) }
  } else if (!standalone) {
    throw new Error('TextInput must be used inside a Form component')
  }
  const Input = (
    <>
      {leadingIcon && (
        <div className={inputLeadingIcon}>
          <FontAwesomeIcon icon={leadingIcon} />
        </div>
      )}
      {leadingText && <div className={inputLeadingText}>{leadingText}</div>}
      <input
        type={
          type === 'password' && passwordVisibility ? 'text' : (type ?? 'text')
        }
        className={clsx(
          input,
          type === 'password' && inputPassword,
          type === 'date' && inputDate,
          error !== undefined && inputError,
          small && inputSmall,
          inputSize === 'small' && inputSmall,
          inputSize === 'extraSmall' && inputExtraSmall,
          leadingIcon && inputHasLeadingIcon,
          trailingIcon && inputHasTrailingIcon,
          disabled && inputDisabled,
          attributes.className
        )}
        aria-invalid={error !== null ? 'true' : 'false'}
        aria-label={label}
        disabled={disabled}
        id={id ?? name}
        {...attributes}
      />
    </>
  )
  if (type === 'hidden') {
    return Input
  }
  if (!wrapper) {
    return <div className={clsx(inputField, noWrapperClassName)}>{Input}</div>
  }
  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div
        className={clsx(
          inputField,
          disabled && inputDisabled,
          leadingText && inputHasLeadingText,
          trailingText && inputHasTrailingText,
          currencyInputName && inputHasCurrencyDropdown
        )}
      >
        {Input}
        {type === 'password' && (
          <div
            className={clsx(
              passwordVisibilityTrigger,
              error && passwordVisibilityTriggerOnError
            )}
            onClick={() => setPasswordVisibility(!passwordVisibility)}
          >
            <FontAwesomeIcon icon={!passwordVisibility ? faEyeSlash : faEye} />
          </div>
        )}
        {trailingIcon && (
          <div className={inputTrailingIcon}>
            <FontAwesomeIcon icon={trailingIcon} />
          </div>
        )}
        {trailingText && (
          <div className={inputTrailingText}>{trailingText}</div>
        )}
        {currencyInputName && (
          <Dropdown
            name={currencyInputName}
            narrow
            required
            control={control}
            register={register}
            watch={watch}
            setValue={setValue}
            defaultValue={String(currencyOptions[0]?.name)}
            options={currencyOptions.map((c) => ({
              label: c.name,
              value: String(c.id)
            }))}
          />
        )}
      </div>
      {hasPasswordHint === true && control !== undefined && (
        <PasswordHintInput control={control} />
      )}
      {error !== undefined && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
